import { useState, useEffect } from 'react';
import fetchEndpoint from '../services/fetchEndpoint';
import { DATASETS } from '../components/Constants';
import { getId } from '../components/Constants';

interface DetailContentState {
  dataSource: any;
  vocabulary: string[];
  tipo: string;
  link: string;
  loading: boolean;
  error: Error | null;
}

interface SidebarDetailContentParams {
  selectedInfoItem: any;
  view: string;
  authorization: string;
  isAuthenticated: boolean;
  onAuthStateChange?: (isAuthenticated: boolean) => void;
}

/**
 * Custom hook to manage sidebar detail content fetching and state.
 * Extracts logic from SideBarContent component.
 *
 * Handles fetching different entity types (diseases, genes, variants, chemicals)
 * and manages related vocabulary and link generation.
 */
export function useSidebarDetailContent({
  selectedInfoItem,
  view,
  authorization,
  isAuthenticated,
  onAuthStateChange,
}: SidebarDetailContentParams): DetailContentState {
  const [dataSource, setDataSource] = useState<any>([]);
  const [vocabulary, setVocabulary] = useState<string[]>([]);
  const [tipo, setTipo] = useState('');
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setTipo('');
    setError(null);

    if (selectedInfoItem === null) {
      return;
    }

    const viewId = view;
    const id = getId(selectedInfoItem);
    const unauth = isAuthenticated ? '' : '?skey=unauth';

    setLoading(true);

    const fetchDetailData = async () => {
      try {
        switch (viewId) {
          case DATASETS.DISEASES: {
            const endpoint = `api/disease/detail/${id}${unauth}`;
            const result = await fetchEndpoint(endpoint, authorization, onAuthStateChange);
            if (result?.status === 'OK') {
              setDataSource(result);
              const linkUMLSCUI =
                'http://linkedlifedata.com/resource/umls/id/' + result.payload.diseaseUMLSCUI;
              setLink(linkUMLSCUI);
              const vocabularies = [
                ...new Set(result.payload.diseaseCodes.map((v: any) => v.vocabulary)),
              ] as string[];
              setVocabulary(vocabularies);
              setTipo(view);
            }
            break;
          }

          case DATASETS.GENES: {
            const endpoint = `api/gene/detail/${id}${unauth}`;
            const result = await fetchEndpoint(endpoint, authorization, onAuthStateChange);
            if (result?.status === 'OK') {
              setDataSource(result);
              const linkGene = 'http://www.ncbi.nlm.nih.gov/gene/' + result.payload.geneID;
              setLink(linkGene);
              setTipo(viewId);
            }
            break;
          }

          case DATASETS.VARIANTS: {
            const endpoint = `api/variant/detail/${id}${unauth}`;
            const result = await fetchEndpoint(endpoint, authorization, onAuthStateChange);
            if (result?.status === 'OK') {
              setDataSource(result);
              setTipo(viewId);
            }
            break;
          }

          case DATASETS.CHEMICALS: {
            const endpoint = `api/chem/detail/${id}${unauth}`;
            const result = await fetchEndpoint(endpoint, authorization, onAuthStateChange);
            if (result?.status === 'OK') {
              setDataSource(result.payload);
              setTipo(viewId);
            }
            break;
          }

          default:
            return;
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch detail content');
        setError(error);
        console.error('Error fetching sidebar detail content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetailData();
  }, [selectedInfoItem, view, authorization, isAuthenticated, onAuthStateChange]);

  return {
    dataSource,
    vocabulary,
    tipo,
    link,
    loading,
    error,
  };
}
